import React, { useState, useEffect } from 'react';
import { X, Briefcase, TrendingUp, FileText, MapPin, Calendar, Clock, DollarSign, Eye, CheckCircle2, AlertCircle, GraduationCap, Award, Mail, Hash, Building, User, BookOpen, Target, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CustomPagination from "views/client/pagination";
import axios from "axios";

const colors = {
  primary: '#0017E7',
  secondary: '#F4E1F7',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  text: '#1e293b',
  lightText: '#64748b',
  border: '#e2e8f0',
};

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return first + last || 'U';
};

const StatusChip = ({ label, status }) => {
  const statusColors = {
    active: { bg: '#d1fae5', color: '#059669', border: '#10b981' },
    inactive: { bg: '#fee2e2', color: '#dc2626', border: '#ef4444' },
    pending: { bg: '#fef3c7', color: '#d97706', border: '#f59e0b' },
    reviewed: { bg: '#dbeafe', color: '#1d4ed8', border: '#3b82f6' },
  };
  const colorSet = statusColors[status] || statusColors.active;

  return (
    <span
      style={{
        backgroundColor: colorSet.bg,
        color: colorSet.color,
        border: `1.5px solid ${colorSet.border}40`,
        fontWeight: 700,
        fontSize: '11px',
        height: '24px',
        borderRadius: '8px',
        padding: '4px 12px',
        display: 'inline-block',
      }}
    >
      {label.toUpperCase()}
    </span>
  );
};

const TypeChip = ({ type, t }) => {
  const typeConfig = {
    j: { label: t('superAdmin.recruiterDetailsDialog.job'), bg: '#dbeafe', color: '#1d4ed8' },
    i: { label: t('superAdmin.recruiterDetailsDialog.internship'), bg: '#fef3c7', color: '#d97706' },
  };
  const config = typeConfig[type] || typeConfig.j;

  return (
    <span
      style={{
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: 700,
        fontSize: '11px',
        height: '24px',
        borderRadius: '8px',
        padding: '4px 12px',
        display: 'inline-block',
      }}
    >
      {config.label}
    </span>
  );
};

const PostCard = ({ post, t }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={{
        borderRadius: '16px',
        border: `2px solid ${colors.secondary}`,
        boxShadow: '0 2px 12px rgba(0, 23, 231, 0.08)',
        transition: 'all 0.3s ease',
        backgroundColor: 'white',
        padding: '20px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 23, 231, 0.12)';
        e.currentTarget.style.borderColor = colors.primary;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 23, 231, 0.08)';
        e.currentTarget.style.borderColor = colors.secondary;
      }}
    >
      {/* Header Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontWeight: 700, color: colors.text, marginBottom: '8px', fontSize: '18px', lineHeight: 1.3 }}>
            {post.title}
          </h3>
          {post.reference && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <Hash size={14} color={colors.lightText} />
              <span style={{ fontSize: '12px', color: colors.lightText, fontWeight: 600 }}>
                {t('superAdmin.recruiterDetailsDialog.reference')} {post.reference}
              </span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexDirection: 'column', alignItems: 'flex-end' }}>
          <StatusChip label={post.archived ? t('superAdmin.recruiterDetailsDialog.inactive') : t('superAdmin.recruiterDetailsDialog.active')} status={post.archived ? 'inactive' : 'active'} />
          <TypeChip type={post.offerType} t={t} />
        </div>
      </div>

      {/* Location & Mode */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {post.localisation && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <MapPin size={14} color={colors.lightText} />
              <span style={{ fontSize: '13px', color: colors.lightText, fontWeight: 600 }}>
                {post.localisation}
              </span>
            </div>
            {post.mode && <span style={{ color: colors.lightText }}>•</span>}
          </>
        )}
        {post.mode && (
          <span style={{ fontSize: '13px', color: colors.lightText, fontWeight: 600 }}>
            {post.mode}
          </span>
        )}
      </div>

      {/* Description */}
      {post.description && (
        <div style={{
          fontSize: '13px',
          color: colors.text,
          marginBottom: '16px',
          lineHeight: 1.6,
          maxHeight: expanded ? 'none' : '60px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: expanded ? 'unset' : 3,
          WebkitBoxOrient: 'vertical',
        }}
        dangerouslySetInnerHTML={{ __html: post.description }}
        />
      )}
      {post.description && post.description.length > 150 && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            fontSize: '12px',
            color: colors.primary,
            fontWeight: 600,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            padding: 0,
            marginBottom: '12px',
          }}
        >
          {expanded ? t('superAdmin.recruiterDetailsDialog.showLess') : t('superAdmin.recruiterDetailsDialog.showMore')}
        </button>
      )}

      {/* Education & Experience Requirements */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {post.highest_diploma && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '10px',
                backgroundColor: `${colors.secondary}40`,
                border: `1px solid ${colors.secondary}`,
              }}
            >
              <Award size={14} color={colors.primary} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: colors.text }}>
                {post.highest_diploma}
              </span>
            </div>
          )}
          {post.field_of_study && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '10px',
                backgroundColor: `${colors.secondary}40`,
                border: `1px solid ${colors.secondary}`,
              }}
            >
              <BookOpen size={14} color={colors.primary} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: colors.text }}>
                {post.field_of_study}
              </span>
            </div>
          )}
          {post.age_experience > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '10px',
                backgroundColor: '#dbeafe40',
                border: `1px solid #dbeafe`,
              }}
            >
              <Target size={14} color={colors.primary} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: colors.text }}>
                {post.age_experience} {t('superAdmin.recruiterDetailsDialog.ann_experience')}

              </span>
            </div>
          )}
        </div>
      </div>

      {/* Skills/Requirements */}
      {post.requirements && Array.isArray(post.requirements) && post.requirements.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: colors.lightText, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t('superAdmin.recruiterDetailsDialog.skillsRequired')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {post.requirements.slice(0, expanded ? undefined : 5).map((skill, idx) => (
              <span
                key={idx}
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: colors.primary,
                  backgroundColor: `${colors.primary}10`,
                  padding: '4px 10px',
                  borderRadius: '6px',
                  border: `1px solid ${colors.primary}20`,
                }}
              >
                {skill}
              </span>
            ))}
            {!expanded && post.requirements.length > 5 && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: colors.lightText,
                  padding: '4px 10px',
                }}
              >
                +{post.requirements.length - 5} {t('superAdmin.recruiterDetailsDialog.showMore').toLowerCase()}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Salary & Dates Section */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {post.salary && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 14px',
              borderRadius: '12px',
              backgroundColor: '#d1fae540',
              border: `1px solid ${colors.success}40`,
            }}
          >
            <DollarSign size={16} color={colors.success} />
            <span style={{ fontSize: '13px', fontWeight: 700, color: colors.success }}>
              {post.salary} {post.currency || ''}
            </span>
          </div>
        )}
        {post.date && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 14px',
              borderRadius: '12px',
              backgroundColor: `${colors.secondary}40`,
              border: `1px solid ${colors.secondary}`,
            }}
          >
            <Calendar size={16} color={colors.primary} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: colors.text }}>
              {t('superAdmin.recruiterDetailsDialog.postedDate')} {new Date(post.date).toLocaleDateString()}
            </span>
          </div>
        )}
        {post.deadline && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 14px',
              borderRadius: '12px',
              backgroundColor: '#fef3c740',
              border: `1px solid ${colors.warning}40`,
            }}
          >
            <Clock size={16} color={colors.warning} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: colors.warning }}>
              {t('superAdmin.recruiterDetailsDialog.deadline')} {new Date(post.deadline).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>

      {/* Contact Email */}
      {post.recruiter_email && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: '10px',
            backgroundColor: `${colors.secondary}30`,
            border: `1px solid ${colors.secondary}`,
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Mail size={14} color={colors.primary} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: colors.text }}>
              {post.recruiter_email}
            </span>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div style={{ display: 'flex', gap: '24px', paddingTop: '12px', borderTop: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FileText size={16} color={colors.primary} />
          <span style={{ fontWeight: 700, color: colors.text, fontSize: '14px' }}>
            {post.total_applications || post.applications || 0}
          </span>
          <span style={{ fontSize: '13px', color: colors.lightText }}>
            {t('superAdmin.recruiterDetailsDialog.applicationsCount')}
          </span>
        </div>
        {post.scraped && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Building size={16} color={colors.lightText} />
            <span style={{ fontSize: '13px', color: colors.lightText, fontWeight: 600 }}>
              {t('superAdmin.recruiterDetailsDialog.scraped')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const ApplicationCard = ({ app, t, handleResumeClick }) => (
  <div
    style={{
      borderRadius: '16px',
      border: `2px solid ${colors.secondary}`,
      boxShadow: '0 2px 12px rgba(0, 23, 231, 0.08)',
      transition: 'all 0.3s ease',
      backgroundColor: 'white',
      padding: '20px',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 23, 231, 0.12)';
      e.currentTarget.style.borderColor = colors.primary;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 23, 231, 0.08)';
      e.currentTarget.style.borderColor = colors.secondary;
    }}
  >
    <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${colors.primary} 0%, #6366f1 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: 800,
          color: 'white',
        }}
      >
        {getInitials(app.candidate_name?.split(' ')[0], app.candidate_name?.split(' ')[1])}
      </div>
      <div style={{ flex: 1 }}>
        <h4 style={{ fontWeight: 700, color: colors.text, marginBottom: '4px', fontSize: '16px' }}>
          {app.candidate_name}
        </h4>
        <p style={{ fontSize: '13px', color: colors.lightText, fontWeight: 600, margin: 0 }}>
          {app.email}
        </p>
      </div>
      <StatusChip label={app.status} status={app.status} />
    </div>

    <div style={{ height: '1px', backgroundColor: colors.border, margin: '12px 0' }} />

    <p style={{ fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '12px' }}>
      {t('superAdmin.recruiterDetailsDialog.appliedFor')} {app.position}
    </p>

    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      {(app.resume_path || app.resume) ? (
        <button
          onClick={() => handleResumeClick(app)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 14px',
            borderRadius: '12px',
            backgroundColor: `${colors.primary}10`,
            border: `1px solid ${colors.primary}20`,
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            color: colors.primary,
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = `${colors.primary}20`;
            e.currentTarget.style.borderColor = `${colors.primary}40`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = `${colors.primary}10`;
            e.currentTarget.style.borderColor = `${colors.primary}20`;
          }}
        >
          <Download size={14} />
          <span>{t('superAdmin.recruiterDetailsDialog.downloadCV', 'Download CV')}</span>
        </button>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 14px',
            borderRadius: '12px',
            backgroundColor: `${colors.lightText}20`,
            border: `1px solid ${colors.lightText}30`,
            fontSize: '13px',
            fontWeight: 600,
            color: colors.lightText,
            opacity: 0.6,
          }}
        >
          <Download size={14} />
          <span>{t('superAdmin.recruiterDetailsDialog.downloadCV', 'Download CV')}</span>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          borderRadius: '12px',
          backgroundColor: `${colors.secondary}40`,
          border: `1px solid ${colors.secondary}`,
        }}
      >
        <Clock size={14} color={colors.lightText} />
        <span style={{ fontSize: '13px', fontWeight: 600, color: colors.text }}>
          {new Date(app.applied_at || app.date).toLocaleDateString()}
        </span>
      </div>
    </div>
  </div>
);

const RecruiterDetailsDialog = ({ open, onClose, recruiter }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [posts, setPosts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [postsPagination, setPostsPagination] = useState({ page: 0, page_size: 9, count: 0, total_pages: 0 });
  const [appsPagination, setAppsPagination] = useState({ page: 0, page_size: 9, count: 0, total_pages: 0 });

  const handleResumeClick = async (app) => {
    console.log("resumePath22", app);
    if (!app.resume_path && !app.resume) {
      alert(t('superAdmin.recruiterDetailsDialog.resumeNotAvailable', 'Resume not available'));
      return;
    }

    try {
      const resumePath = app.resume_path || app.resume;
      const apiUrl = process.env.REACT_APP_API_URL;

      const normalizedPath = resumePath.replace(/\\/g, "/");
      let fullPath;

      if (app.type === 'scraped') {
        // For scraped applications
        fullPath = normalizedPath.startsWith(apiUrl)
          ? normalizedPath
          : `${apiUrl}media/${normalizedPath}`;
      } else {
        // For regular applications
        fullPath = normalizedPath.startsWith(apiUrl)
          ? normalizedPath
          : `${apiUrl}media/${normalizedPath}`;
      }

      const response = await axios.post(
        `${apiUrl}ai/search_resume/`,
        { resume_url: fullPath },
        { responseType: "blob" }
      );

      if (response.headers["content-type"].includes("application/pdf")) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      } else {
        console.error(
          "Unexpected response type:",
          response.headers["content-type"]
        );
      }
    } catch (error) {
      console.error("Resume open error:", error);
      console.error("Error details:", error.response || error.message);
      alert(
        t('superAdmin.recruiterDetailsDialog.failedToOpenResume', 'Failed to open resume. Please try again later.')
      );
    }
  };

  useEffect(() => {
    if (open && recruiter) {
      fetchRecruiterData();
    }
  }, [open, recruiter, activeTab]);

  const handlePostsPageChange = (event, newPage) => {
    setPostsPagination(prev => ({ ...prev, page: newPage }));
    fetchRecruiterData(newPage + 1, postsPagination.page_size);
  };

  const handlePostsRowsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPostsPagination(prev => ({ ...prev, page_size: newPageSize, page: 0 }));
    fetchRecruiterData(1, newPageSize);
  };

  const handleAppsPageChange = (event, newPage) => {
    setAppsPagination(prev => ({ ...prev, page: newPage }));
    fetchRecruiterData(newPage + 1, appsPagination.page_size);
  };

  const handleAppsRowsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setAppsPagination(prev => ({ ...prev, page_size: newPageSize, page: 0 }));
    fetchRecruiterData(1, newPageSize);
  };

  const fetchRecruiterData = async (page = 1, pageSize = 9) => {
    if (!recruiter) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const baseUrl = process.env.REACT_APP_API_URL || '';

      if (activeTab === 0 || activeTab === 1) {
        const postsResponse = await fetch(`${baseUrl}authentication/${recruiter.id}/posts/?page=${page}&page_size=${pageSize}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (postsResponse.ok) {
          const postsData = await postsResponse.json();
          setPosts(postsData.results || []);
          setPostsPagination({
            page: postsData.current_page - 1, // MUI pagination is 0-based
            page_size: postsData.page_size,
            count: postsData.count,
            total_pages: postsData.total_pages
          });
        }
      }

      if (activeTab === 2) {
        const appsResponse = await fetch(`${baseUrl}authentication/${recruiter.id}/applications/?page=${page}&page_size=${pageSize}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (appsResponse.ok) {
          const appsData = await appsResponse.json();
          setApplications(appsData.results || []);
          setAppsPagination({
            page: appsData.current_page - 1, // MUI pagination is 0-based
            page_size: appsData.page_size,
            count: appsData.count,
            total_pages: appsData.total_pages
          });
        }
      }
    } catch (error) {
      console.error('Error fetching recruiter data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!open || !recruiter) return null;

  const activePosts = posts.filter(post => !post.archived);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          maxWidth: '1400px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, #3b3bff 100%)`,
            padding: '16px 32px',
            position: 'relative',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'white',
                border: '4px solid white',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: 800,
                color: colors.primary,
              }}
            >
              {getInitials(recruiter.first_name, recruiter.last_name)}
            </div>
            <div>
              <h2 style={{ fontWeight: 800, color: 'white', marginBottom: '8px', fontSize: '24px' }}>
                {recruiter.first_name} {recruiter.last_name}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '14px' }}>
                  @{recruiter.username}
                </span>
                <CheckCircle2 size={16} color="white" fill="white" />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', backgroundColor: 'white', borderBottom: `1px solid ${colors.border}` }}>
            {[
              { label: t('superAdmin.recruiterDetailsDialog.allPosts'), icon: Briefcase },
              { label: t('superAdmin.recruiterDetailsDialog.activePosts'), icon: TrendingUp },
              { label: t('superAdmin.recruiterDetailsDialog.applications'), icon: FileText },
            ].map((tab, idx) => {
              const Icon = tab.icon;
              const isActive = activeTab === idx;
              return (
                <button
                  key={idx}
                  onClick={() => setActiveTab(idx)}
                  style={{
                    flex: 1,
                    padding: '16px 24px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    color: isActive ? colors.primary : colors.lightText,
                    fontWeight: 600,
                    fontSize: '15px',
                    borderBottom: isActive ? `3px solid ${colors.primary}` : '3px solid transparent',
                    transition: 'all 0.2s',
                  }}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <button
            onClick={onClose}
            style={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: 'none',
              borderRadius: '8px',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '24px',
            backgroundColor: '#fafafa',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: `4px solid ${colors.secondary}`,
                borderTopColor: colors.primary,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px',
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <div style={{ fontSize: '16px', color: colors.lightText, fontWeight: 600 }}>{t('superAdmin.recruiterDetailsDialog.loading')}</div>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))',
                gap: '20px',
              }}
            >
              {activeTab === 0 && posts.map(post => <PostCard key={post.id} post={post} t={t} />)}
              {activeTab === 1 && activePosts.map(post => <PostCard key={post.id} post={post} t={t} />)}
              {activeTab === 2 && applications.map(app => <ApplicationCard key={app.id} app={app} t={t} handleResumeClick={handleResumeClick} />)}
            </div>
          )}

          {!loading && ((activeTab === 0 && posts.length === 0) ||
            (activeTab === 1 && activePosts.length === 0) ||
            (activeTab === 2 && applications.length === 0)) && (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
              <AlertCircle size={48} color={colors.lightText} style={{ marginBottom: '16px' }} />
              <h3 style={{ color: colors.lightText, fontWeight: 600, margin: 0 }}>
                {t('superAdmin.recruiterDetailsDialog.noDataAvailable')}
              </h3>
            </div>
          )}

          {/* Pagination */}
          {!loading && ((activeTab === 0 && posts.length > 0) || (activeTab === 1 && activePosts.length > 0)) && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0', backgroundColor: '#fafafa' }}>
              <CustomPagination
                page={postsPagination.page}
                handleChangePage={handlePostsPageChange}
                rowsPerPage={postsPagination.page_size}
                onRowsPerPageChange={handlePostsRowsPerPageChange}
                count={postsPagination.count}
              />
            </div>
          )}

          {!loading && activeTab === 2 && applications.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0', backgroundColor: '#fafafa' }}>
              <CustomPagination
                page={appsPagination.page}
                handleChangePage={handleAppsPageChange}
                rowsPerPage={appsPagination.page_size}
                onRowsPerPageChange={handleAppsRowsPerPageChange}
                count={appsPagination.count}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecruiterDetailsDialog;